import { useEffect } from "react";

import classNames from "classnames";
import useQueryParams from "neetocommons/react-utils/useQueryParams";
import { NoData } from "neetoui";
import propTypes from "prop-types";
import { isEmpty } from "ramda";
import { useTranslation } from "react-i18next";

import Header from "components/Header";

import Category from "./Category";
import { filterCategories, scrollIntoSettingCategory } from "./utils";

const Settings = ({
  isTitleHidden = false,
  isSearchHidden = false,
  title,
  headerProps = {},
  categories = [],
  className = "",
}) => {
  const { t } = useTranslation();

  const { category: categoryParam, searchTerm = "" } = useQueryParams();

  const filteredCategories = filterCategories({ categories, searchTerm });

  const isHeaderVisible = !isSearchHidden || !isTitleHidden;

  const headerTitle = title ?? t("neetoMolecules.settingsPage.title");

  useEffect(() => {
    scrollIntoSettingCategory(categoryParam);
  }, [categoryParam]);

  return (
    <div
      className={classNames("neeto-molecules-container", className, {
        "h-auto overflow-y-auto": isTitleHidden,
      })}
    >
      {isHeaderVisible && (
        <Header
          className={classNames({ "min-h-0 pb-4 pt-8": isTitleHidden })}
          title={!isTitleHidden && headerTitle}
          searchProps={
            isSearchHidden
              ? null
              : {
                  className: "w-72",
                  placeholder: t(
                    "neetoMolecules.settingsPage.placeholder.search"
                  ),
                }
          }
          {...headerProps}
        />
      )}
      {isEmpty(filteredCategories) ? (
        <div
          className="flex h-full w-full flex-grow items-center justify-center"
          data-testid="settings-no-data"
        >
          <NoData title={t("neetoMolecules.settingsPage.noData")} />
        </div>
      ) : (
        <div className="w-full">
          {filteredCategories.map(({ id, label, items, isIntegration }) => (
            <Category {...{ id, isIntegration, items, label }} key={id} />
          ))}
        </div>
      )}
    </div>
  );
};

Settings.propTypes = {
  /**
   * Whether to hide the title or not.
   */
  isTitleHidden: propTypes.bool,
  /**
   * This can be used to customize the title.
   */
  title: propTypes.string,
  /**
   * Whether to hide the search bar or not.
   */
  isSearchHidden: propTypes.bool,
  /**
   * An array of setting categories to be displayed.
   */
  categories: propTypes.array,
  /**
   * Additional classes to be added to the container.
   */
  className: propTypes.string,
  /**
   * Props to be passed to the header component.
   */
  headerProps: propTypes.object,
};

export default Settings;
